import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Select,
  Option,
  Table,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  FormControl,
  FormLabel,
  Stack,
  LinearProgress,
} from '@mui/joy';
import {
  DownloadSimple as Download,
  Printer as Print,
  CaretDown as ExpandMore,
  CaretUp as ExpandLess,
  TrendUp as TrendingUp,
  TrendDown as TrendingDown,
} from '@phosphor-icons/react';
import { apiAccountingClient } from '../../../lib/apiAccounting';
import type { ProfitLoss as ProfitLossType } from '../../../types/accounting';

export const ProfitLoss: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profitLoss, setProfitLoss] = useState<ProfitLossType | null>(null);
  const [comparativePL, setComparativePL] = useState<ProfitLossType | null>(null);
  
  // Report parameters
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [comparePeriod, setComparePeriod] = useState<'none' | 'previous' | 'year'>('none');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['revenue', 'expenses']));
  
  useEffect(() => {
    fetchProfitLoss();
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (comparePeriod !== 'none') {
      fetchComparativePL();
    } else {
      setComparativePL(null);
    }
  }, [comparePeriod, dateFrom, dateTo]);

  const fetchProfitLoss = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiAccountingClient.getProfitLoss({
        start_date: dateFrom,
        end_date: dateTo,
      });
      // Sanitize numeric values to prevent NaN
      if (response) {
        response.total_revenue = isNaN(response.total_revenue) ? 0 : response.total_revenue;
        response.total_expenses = isNaN(response.total_expenses) ? 0 : response.total_expenses;
        response.net_income = isNaN(response.net_income) ? 0 : response.net_income;
        // Set total_cogs to 0 if not provided by API
        response.total_cogs = response.total_cogs || 0;
      }
      setProfitLoss(response);
    } catch (err) {
      console.error('Failed to fetch P&L:', err);
      setError('Failed to load profit & loss statement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchComparativePL = async () => {
    try {
      let compareFrom: string;
      let compareTo: string;
      
      if (comparePeriod === 'previous') {
        // Previous period of same length
        const from = new Date(dateFrom);
        const to = new Date(dateTo);
        const daysDiff = Math.floor((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
        
        const newTo = new Date(from);
        newTo.setDate(newTo.getDate() - 1);
        const newFrom = new Date(newTo);
        newFrom.setDate(newFrom.getDate() - daysDiff);
        
        compareFrom = newFrom.toISOString().split('T')[0];
        compareTo = newTo.toISOString().split('T')[0];
      } else {
        // Same period last year
        const from = new Date(dateFrom);
        const to = new Date(dateTo);
        from.setFullYear(from.getFullYear() - 1);
        to.setFullYear(to.getFullYear() - 1);
        
        compareFrom = from.toISOString().split('T')[0];
        compareTo = to.toISOString().split('T')[0];
      }
      
      const response = await apiAccountingClient.getProfitLoss({
        start_date: compareFrom,
        end_date: compareTo,
      });
      // Sanitize numeric values to prevent NaN
      if (response) {
        response.total_revenue = isNaN(response.total_revenue) ? 0 : response.total_revenue;
        response.total_expenses = isNaN(response.total_expenses) ? 0 : response.total_expenses;
        response.net_income = isNaN(response.net_income) ? 0 : response.net_income;
        // Set total_cogs to 0 if not provided by API
        response.total_cogs = response.total_cogs || 0;
      }
      setComparativePL(response);
    } catch (err) {
      console.error('Failed to fetch comparative P&L:', err);
    }
  };

  const handleExportExcel = async () => {
    try {
      const blob = await apiAccountingClient.exportProfitLoss({
        start_date: dateFrom,
        end_date: dateTo,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `profit_loss_${dateFrom}_${dateTo}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export:', err);
      alert('Failed to export profit & loss statement');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined || isNaN(amount)) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatPercentage = (amount: number | undefined, total: number | undefined) => {
    if (!amount || !total || total === 0 || isNaN(amount) || isNaN(total)) return '0.0%';
    const percentage = (amount / total) * 100;
    if (isNaN(percentage)) return '0.0%';
    return `${percentage.toFixed(1)}%`;
  };

  const calculateChange = (current: number | undefined, previous: number | undefined) => {
    if (!current || !previous || previous === 0 || isNaN(current) || isNaN(previous)) return null;
    const change = ((current - previous) / Math.abs(previous)) * 100;
    if (isNaN(change)) return null;
    return change;
  };

  const renderAccountRow = (
    account: { account_number: string; account_name: string; amount: number },
    totalRevenue: number,
    compareAccount?: { account_number: string; account_name: string; amount: number },
    indent: number = 0
  ) => {
    const change = compareAccount ? calculateChange(account.amount, compareAccount.amount) : null;
    
    return (
      <tr key={account.account_number}>
        <td style={{ paddingLeft: `${indent * 20 + 8}px` }}>
          <Typography level="body-sm">
            {account.account_number} - {account.account_name}
          </Typography>
        </td>
        <td align="right">
          <Typography level="body-sm">
            {formatCurrency(account.amount)}
          </Typography>
        </td>
        <td align="right">
          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
            {formatPercentage(account.amount, totalRevenue)}
          </Typography>
        </td>
        {comparePeriod !== 'none' && (
          <>
            <td align="right">
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                {formatCurrency(compareAccount?.amount)}
              </Typography>
            </td>
            <td align="right">
              {change !== null && (
                <Chip
                  size="sm"
                  color={change > 0 ? 'success' : change < 0 ? 'danger' : 'neutral'}
                  startDecorator={change > 0 ? <TrendingUp /> : change < 0 ? <TrendingDown /> : null}
                >
                  {change.toFixed(1)}%
                </Chip>
              )}
            </td>
          </>
        )}
      </tr>
    );
  };

  const renderSection = (
    title: string,
    accounts: Array<{ account_number: string; account_name: string; amount: number }> | undefined,
    total: number | undefined,
    totalRevenue: number,
    compareAccounts: Array<{ account_number: string; account_name: string; amount: number }> | undefined,
    compareTotal: number | undefined,
    sectionKey: string,
    isExpense: boolean = false
  ) => {
    const isExpanded = expandedSections.has(sectionKey);
    const change = compareTotal ? calculateChange(total, compareTotal) : null;

    return (
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1,
            bgcolor: 'background.level1',
            borderRadius: 'sm',
            cursor: 'pointer',
          }}
          onClick={() => toggleSection(sectionKey)}
        >
          <Typography level="title-md" sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton size="sm" sx={{ mr: 1 }}>
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {formatPercentage(total, totalRevenue)}
            </Typography>
            <Typography level="title-md">
              {formatCurrency(total)}
            </Typography>
            {comparePeriod !== 'none' && change !== null && (
              <Chip
                size="sm"
                color={
                  isExpense
                    ? change < 0 ? 'success' : change > 0 ? 'danger' : 'neutral'
                    : change > 0 ? 'success' : change < 0 ? 'danger' : 'neutral'
                }
                startDecorator={change > 0 ? <TrendingUp /> : change < 0 ? <TrendingDown /> : null}
              >
                {change.toFixed(1)}%
              </Chip>
            )}
          </Box>
        </Box>

        {isExpanded && (
          <Box sx={{ overflowX: 'auto', mt: 1 }}>
            <Table size="sm" sx={{ minWidth: { xs: 480, md: 0 } }}>
              <tbody>
                {accounts && accounts.map((account) => {
                  const compareAccount = compareAccounts?.find(
                    (ca) => ca.account_number === account.account_number
                  );
                  return renderAccountRow(account, totalRevenue, compareAccount, 0);
                })}
              </tbody>
            </Table>
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !profitLoss) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        {error || 'Failed to load profit & loss statement'}
      </Alert>
    );
  }

  const grossProfit = (profitLoss.total_revenue || 0) - (profitLoss.total_cogs || 0);
  const operatingIncome = grossProfit - (profitLoss.total_expenses || 0);
  const grossMargin = (profitLoss.total_revenue || 0) > 0 ? (grossProfit / (profitLoss.total_revenue || 1)) * 100 : 0;
  const netMargin = (profitLoss.total_revenue || 0) > 0 ? ((profitLoss.net_income || 0) / (profitLoss.total_revenue || 1)) * 100 : 0;

  return (
    <Box className="profit-loss-report">
      {/* Header Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 1.5, mb: 3 }}>
            <Box>
              <Typography level="h2" sx={{ mb: 1, fontSize: { xs: '1.5rem', md: undefined } }}>
                Profit & Loss Statement
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Income Statement for {new Date(dateFrom).toLocaleDateString()} to {new Date(dateTo).toLocaleDateString()}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ '& > button': { flex: { xs: 1, md: '0 0 auto' } } }}>
              <Button
                startDecorator={<Download />}
                variant="outlined"
                color="primary"
                onClick={handleExportExcel}
              >
                Export Excel
              </Button>
              <Button
                startDecorator={<Print />}
                variant="outlined"
                color="primary"
                onClick={handlePrint}
              >
                Print
              </Button>
            </Stack>
          </Box>

          {/* Report Parameters */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
            <FormControl>
              <FormLabel>From Date</FormLabel>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                }}
              />
            </FormControl>

            <FormControl>
              <FormLabel>To Date</FormLabel>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                }}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Compare With</FormLabel>
              <Select
                value={comparePeriod}
                onChange={(e, value) => setComparePeriod(value as any)}
              >
                <Option value="none">No Comparison</Option>
                <Option value="previous">Previous Period</Option>
                <Option value="year">Same Period Last Year</Option>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
              Net Income
            </Typography>
            <Typography 
              level="h3" 
              sx={{ 
                color: profitLoss.net_income >= 0 ? 'success.main' : 'danger.main' 
              }}
            >
              {formatCurrency(profitLoss.net_income)}
            </Typography>
            {comparativePL && (
              <Chip
                size="sm"
                sx={{ mt: 1 }}
                color={
                  calculateChange(profitLoss.net_income, comparativePL.net_income)! > 0
                    ? 'success'
                    : 'danger'
                }
              >
                {calculateChange(profitLoss.net_income, comparativePL.net_income)?.toFixed(1)}%
              </Chip>
            )}
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
              Gross Margin
            </Typography>
            <Typography level="h3">
              {grossMargin.toFixed(1)}%
            </Typography>
            <LinearProgress
              determinate
              value={grossMargin}
              sx={{ mt: 1 }}
              color={grossMargin > 30 ? 'success' : grossMargin > 20 ? 'warning' : 'danger'}
            />
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
              Net Margin
            </Typography>
            <Typography level="h3">
              {netMargin.toFixed(1)}%
            </Typography>
            <LinearProgress
              determinate
              value={Math.abs(netMargin)}
              sx={{ mt: 1 }}
              color={netMargin > 10 ? 'success' : netMargin > 5 ? 'warning' : 'danger'}
            />
          </CardContent>
        </Card>
      </Stack>

      {/* P&L Content */}
      <Card>
        <CardContent>
          {/* Revenue Section */}
          {renderSection(
            'REVENUE',
            profitLoss.revenue,
            profitLoss.total_revenue,
            profitLoss.total_revenue,
            comparativePL?.revenue,
            comparativePL?.total_revenue,
            'revenue',
            false
          )}

          {/* Cost of Goods Sold */}
          {profitLoss.cogs && profitLoss.cogs.length > 0 && (
            <>
              {renderSection(
                'COST OF GOODS SOLD',
                profitLoss.cogs,
                profitLoss.total_cogs,
                profitLoss.total_revenue,
                comparativePL?.cogs,
                comparativePL?.total_cogs,
                'cogs',
                true
              )}
              
              <Box sx={{ bgcolor: 'background.level2', p: 1.5, my: 2, borderRadius: 'sm' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography level="title-md">Gross Profit</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      {formatPercentage(grossProfit, profitLoss.total_revenue)}
                    </Typography>
                    <Typography level="title-md">
                      {formatCurrency(grossProfit)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}

          {/* Operating Expenses */}
          {renderSection(
            'OPERATING EXPENSES',
            profitLoss.expenses,
            profitLoss.total_expenses,
            profitLoss.total_revenue,
            comparativePL?.expenses,
            comparativePL?.total_expenses,
            'expenses',
            true
          )}

          <Box sx={{ bgcolor: 'background.level2', p: 1.5, my: 2, borderRadius: 'sm' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography level="title-md">Operating Income</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  {formatPercentage(operatingIncome, profitLoss.total_revenue)}
                </Typography>
                <Typography level="title-md">
                  {formatCurrency(operatingIncome)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Other Income/Expenses */}
          {profitLoss.other_income && profitLoss.other_income.length > 0 && (
            renderSection(
              'OTHER INCOME',
              profitLoss.other_income,
              profitLoss.total_other_income,
              profitLoss.total_revenue,
              comparativePL?.other_income,
              comparativePL?.total_other_income,
              'other_income',
              false
            )
          )}

          {profitLoss.other_expenses && profitLoss.other_expenses.length > 0 && (
            renderSection(
              'OTHER EXPENSES',
              profitLoss.other_expenses,
              profitLoss.total_other_expenses,
              profitLoss.total_revenue,
              comparativePL?.other_expenses,
              comparativePL?.total_other_expenses,
              'other_expenses',
              true
            )
          )}

          <Divider sx={{ my: 3 }} />

          {/* Net Income */}
          <Box sx={{ bgcolor: 'primary.softBg', p: 2, borderRadius: 'sm' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography level="h4">NET INCOME</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Typography level="title-md" sx={{ color: 'text.secondary' }}>
                  {formatPercentage(profitLoss.net_income, profitLoss.total_revenue)}
                </Typography>
                <Typography 
                  level="h4"
                  sx={{ 
                    color: profitLoss.net_income >= 0 ? 'success.main' : 'danger.main' 
                  }}
                >
                  {formatCurrency(profitLoss.net_income)}
                </Typography>
                {comparePeriod !== 'none' && comparativePL && (
                  <Chip
                    color={
                      calculateChange(profitLoss.net_income, comparativePL.net_income)! > 0
                        ? 'success'
                        : 'danger'
                    }
                    startDecorator={
                      calculateChange(profitLoss.net_income, comparativePL.net_income)! > 0
                        ? <TrendingUp />
                        : <TrendingDown />
                    }
                  >
                    {calculateChange(profitLoss.net_income, comparativePL.net_income)?.toFixed(1)}%
                  </Chip>
                )}
              </Box>
            </Box>
          </Box>

          {/* Report Metadata */}
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
              Generated on {new Date().toLocaleString()} • 
              Report Period: {new Date(dateFrom).toLocaleDateString()} to {new Date(dateTo).toLocaleDateString()}
              {comparePeriod !== 'none' && ` • Comparing with ${comparePeriod === 'previous' ? 'previous period' : 'same period last year'}`}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Print Styles */}
      <style>{`
        @media print {
          .profit-loss-report {
            padding: 20px;
          }
          button, .MuiButton-root {
            display: none !important;
          }
          input[type="date"], select {
            display: none !important;
          }
        }
      `}</style>
    </Box>
  );
};